import { useCallback, useEffect, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { JumpToLatest } from "@/components/molecules/testing/JumpToLatest";
import { StillWorking } from "@/components/molecules/testing/ThinkingPanel";
import { useChatAutoscroll } from "@/hooks/testing/useChatAutoscroll";
import {
	CHAT_COLUMN_STOPS,
	useContainerBreakpoint,
} from "@/hooks/ui/useContainerBreakpoint";
import type { useTestingLayout } from "@/hooks/testing/useTestingLayout";
import type { RenderedMessage, TestingChatActivity } from "@/stores/testingStore";
import { groupMessagesIntoTurns, isRunAnchorMessage } from "./chatTimeline";
import { ChatEmptyCta } from "./ChatEmptyState";
import { ChatSessionSummary } from "./ChatSessionSummary";
import { ClarificationPrompt } from "./ClarificationPrompt";
import { DestructiveRunDialog } from "./DestructiveRunDialog";
import { ExecutionPlanCards } from "./ExecutionPlanCards";
import { RunLiveFeed } from "./ExecutionFeed";
import { HealDeclinedNote, HealProposalCard } from "./HealProposalCard";
import { RunResultCard } from "./RunResult";
import { TestingChatTurn, type PlanBindings } from "./TestingChatTurn";
import { TestingComposer } from "./TestingComposer";

type VM = ReturnType<typeof useTestingLayout>;

/** Stable empty array for turns that are not streaming — a fresh `[]` would break their memo. */
const NO_ACTIVITY: TestingChatActivity[] = [];

export function TestingChatPanel({ vm }: { vm: VM }) {
	// The chat column is resizable and independent of the viewport, so its internals (plan preview
	// table, chip grids) are styled off ITS width via `group-data-[cw-*]/chat:` variants.
	const { ref: columnRef, attrs: columnAttrs } =
		useContainerBreakpoint<HTMLDivElement>(CHAT_COLUMN_STOPS);

	const {
		chat,
		running,
		runningHere,
		healingHere,
		canChat,
		planningActive,
		liveEvents,
		reconnectEvents,
		runResult,
		hasFreshServerRun,
		runStartedAtMs,
		selectedPlan,
		selectedPlanId,
		selectedPlanPreview,
		clarificationEvent,
		completedRuns,
		invalidateRunsForPlan,
		planAnchors,
		runsForPlan,
		canSelfHealRun,
		draft,
		setDraft,
		editingPlanId,
		editingPlanName,
		plans,
		updatePlanMutation,
		verifyPlan,
		verifyPlanById,
		stopping,
		history,
		onChatSubmit,
		onHeal,
		onStop,
		editPlanInChat,
		runPlanById,
		applyRecommendationInChat,
		startNewTestFromSuggestion,
		cancelEditMode,
		runBlockedReason,
		pendingDestructiveRun,
		confirmDestructiveRun,
		cancelDestructiveRun,
		healDisabled,
		healStage,
		healStatus,
		healProposal,
		healProposalPlanId,
		healNotes,
		healDeclineReason,
		healDeciding,
		hasHealProposal,
		acceptHeal,
		rejectHeal,
		planScrollRequest,
	} = vm;

	const turns = useMemo(() => groupMessagesIntoTurns(chat.messages), [chat.messages]);
	const busyHere = runningHere || healingHere;

	// Follow the conversation, but never yank a reader who has scrolled up. The old panel called
	// scrollTo() on every streamed change unconditionally.
	// Stable references pulled out of objects that are recreated on every render.
	const cancelStreaming = chat.cancel;
	const updatePlan = updatePlanMutation.mutateAsync;

	const { scrollRef, showJump, jumpToLatest } = useChatAutoscroll([
		chat.messages.length,
		chat.streaming,
		liveEvents.length,
		runResult?.run_id,
	]);

	// Selecting a plan in the left "plans in this session" list scrolls its card into view. Keyed on the
	// request NONCE (not the plan id) so re-selecting the same plan scrolls again. The card may not be
	// mounted the moment the request lands — on a reload the target thread is still hydrating — so retry
	// across frames until it appears (also letting the autoscroll layout effect settle first), then give
	// up after ~0.6s so a since-deleted plan doesn't spin forever.
	const scrollNonce = planScrollRequest?.nonce;
	const scrollPlanId = planScrollRequest?.planId;
	useEffect(() => {
		if (!scrollPlanId || scrollNonce == null) return;
		let raf = 0;
		let tries = 0;
		const attempt = () => {
			const el = scrollRef.current?.querySelector<HTMLElement>(
				`[data-plan-card="${CSS.escape(scrollPlanId)}"]`,
			);
			if (el) {
				el.scrollIntoView({ block: "start", behavior: "smooth" });
				return;
			}
			if (++tries <= 40) raf = requestAnimationFrame(attempt);
		};
		raf = requestAnimationFrame(attempt);
		return () => cancelAnimationFrame(raf);
	}, [scrollNonce, scrollPlanId, scrollRef]);

	/**
	 * A heal PROPOSES; it never re-runs the plan or changes the source run's verdict. The proposal (or
	 * the healer's decline) renders under the plan it belongs to, once.
	 */
	const renderHealSlot = useCallback(
		(planId: string) => {
			if (healProposalPlanId !== planId) return null;
			if (hasHealProposal && healProposal) {
				return (
					<HealProposalCard
						proposal={healProposal}
						deciding={healDeciding}
						onAccept={() => void acceptHeal(planId)}
						onReject={() => void rejectHeal(planId)}
					/>
				);
			}
			if (healStatus === "declined") {
				return <HealDeclinedNote reason={healDeclineReason} notes={healNotes} />;
			}
			return null;
		},
		[
			healProposalPlanId,
			hasHealProposal,
			healProposal,
			healDeciding,
			healStatus,
			healDeclineReason,
			healNotes,
			acceptHeal,
			rejectHeal,
		],
	);

	/** Editing a plan's step writes straight through to the plan record. */
	const editPlanStep = useCallback(
		async (planId: string, stepIdx: number, newSay: string) => {
			const plan = plans.find((p) => p.id === planId) ?? selectedPlan;
			if (!plan) return;
			const body = plan.steps as {
				format?: string;
				ir?: { steps?: Array<Record<string, unknown>> };
			};
			if (body.format !== "ir_v1" || !Array.isArray(body.ir?.steps)) return;
			const newSteps = body.ir!.steps.map((s, idx) => (idx === stepIdx ? { ...s, say: newSay } : s));
			await updatePlan({
				planId,
				patch: { steps: { ...body, ir: { ...body.ir, steps: newSteps } } as never },
			});
			// Editing a step changes the plan's spec, so any prior run of it ran the old spec and is now
			// stale — drop it so a previous pass can't linger on the just-edited plan (same fix as the
			// chat-refine plan_updated path).
			invalidateRunsForPlan(planId);
		},
		[plans, selectedPlan, updatePlan, invalidateRunsForPlan],
	);

	/** Everything a plan card needs, resolved against live state rather than the message snapshot. */
	const planBindingsFor = useCallback(
		(message: RenderedMessage): PlanBindings | null => {
			const plan = message.proposedPlan;
			if (!plan) return null;
			const isSelected = plan.id === selectedPlanId;
			return {
				running: runningHere && isSelected,
				stopping: stopping && isSelected,
				planningActive,
				onRun: running ? undefined : () => void runPlanById(plan.id),
				onStop: runningHere && isSelected ? onStop : undefined,
				onEdit: running ? undefined : () => editPlanInChat(plan),
				onEditStep: (stepIdx, newSay) => editPlanStep(plan.id, stepIdx, newSay),
				onVerify: verifyPlanById,
				verifying: verifyPlan.isPending && verifyPlan.variables?.planId === plan.id,
				runBlockedReason: isSelected ? runBlockedReason : null,
			};
		},
		[
			selectedPlanId,
			runningHere,
			stopping,
			planningActive,
			running,
			runPlanById,
			onStop,
			editPlanInChat,
			editPlanStep,
			verifyPlanById,
			verifyPlan.isPending,
			verifyPlan.variables?.planId,
			runBlockedReason,
		],
	);

	/** Run result cards + the heal proposal, anchored under the plan message they belong to. */
	const renderAnchoredResults = useCallback(
		(message: RenderedMessage, flatIndex: number) => {
			const plan = message.proposedPlan;
			if (!plan) return null;
			if (!isRunAnchorMessage(message, flatIndex, planAnchors)) return null;
			const planRuns = runsForPlan(completedRuns, plan.id);
			if (planRuns.length === 0) return null;
			return (
				<>
					{planRuns.map((r, i) => (
						<RunResultCard
							key={r.result.run_id}
							result={r.result}
							healing={healingHere && r.result.run_id === runResult?.run_id}
							onHeal={canSelfHealRun && r.result.run_id === runResult?.run_id ? onHeal : undefined}
							healDisabled={healDisabled}
							healStage={healStage}
							onRunAgain={r.planId ? () => void runPlanById(r.planId) : undefined}
							onApplyInChat={applyRecommendationInChat}
							onStartNewTest={startNewTestFromSuggestion}
							latest={i === planRuns.length - 1}
						/>
					))}
					{renderHealSlot(plan.id)}
				</>
			);
		},
		[
			planAnchors,
			runsForPlan,
			completedRuns,
			healingHere,
			runResult?.run_id,
			canSelfHealRun,
			onHeal,
			healDisabled,
			healStage,
			runPlanById,
			applyRecommendationInChat,
			startNewTestFromSuggestion,
			renderHealSlot,
		],
	);

	const showEmptyState =
		turns.length === 0 && !busyHere && liveEvents.length === 0 && !selectedPlanPreview;
	// A plan opened from the session list has no chat thread in memory — show what it will do rather
	// than an empty column.
	const showPlanOverview =
		turns.length === 0 && !busyHere && liveEvents.length === 0 && Boolean(selectedPlanPreview);

	const composerDisabledReason = !canChat
		? busyHere
			? "This test is running — open another session to keep planning."
			: planningActive
				? "The agent is planning…"
				: "Connect your Salesforce org to start testing."
		: null;

	return (
		<div
			ref={columnRef}
			{...columnAttrs}
			className="group/chat flex min-h-0 min-w-0 flex-1 flex-col"
		>
			{/* Destructive-run confirmation gate — only the RUN is gated, never proposing/viewing. */}
			{pendingDestructiveRun && (
				<DestructiveRunDialog
					actions={pendingDestructiveRun.actions}
					planName={
						plans.find((p) => p.id === pendingDestructiveRun.planId)?.name ??
						(selectedPlan?.id === pendingDestructiveRun.planId ? selectedPlan?.name : null)
					}
					onConfirm={confirmDestructiveRun}
					onCancel={cancelDestructiveRun}
				/>
			)}

			<div
				ref={scrollRef}
				aria-label="Test conversation"
				className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
			>
				{showEmptyState && <ChatEmptyCta onSuggestion={startNewTestFromSuggestion} />}

				{showPlanOverview && selectedPlan && selectedPlanPreview && (
					<div className="tst-turn tst-measure flex flex-col gap-3">
						<ExecutionPlanCards
							plan={selectedPlan}
							preview={selectedPlanPreview}
							running={false}
							events={[]}
							runResult={runResult}
						/>
						{runsForPlan(completedRuns, selectedPlan.id).map((r, i, all) => (
							<RunResultCard
								key={r.result.run_id}
								result={r.result}
								healing={healingHere && r.result.run_id === runResult?.run_id}
								onHeal={canSelfHealRun && r.result.run_id === runResult?.run_id ? onHeal : undefined}
								healDisabled={healDisabled}
								healStage={healStage}
								onRunAgain={r.planId ? () => void runPlanById(r.planId) : undefined}
								onApplyInChat={applyRecommendationInChat}
								onStartNewTest={startNewTestFromSuggestion}
								latest={i === all.length - 1}
							/>
						))}
						{renderHealSlot(selectedPlan.id)}
					</div>
				)}

				{turns.map((turn, i) => {
					// Only the LAST turn can be in flight; earlier turns render their own captured trail
					// and must not receive the changing live props, or their memo never holds.
					const isLive = chat.streaming && i === turns.length - 1;
					return (
						<TestingChatTurn
							key={turn.id}
							turn={turn}
							planBindingsFor={planBindingsFor}
							renderAnchoredResults={renderAnchoredResults}
							streaming={isLive}
							phase={isLive ? chat.phase : null}
							liveActivity={isLive ? chat.activity : NO_ACTIVITY}
						/>
					);
				})}

				{/* The browser run. It outlives the planner turn that produced the plan, so it is its own
				    block below the thread rather than something wedged inside a message. */}
				{(busyHere || liveEvents.length > 0) && !runResult && (
					<div className="tst-turn tst-measure">
						<RunLiveFeed events={liveEvents} isActive={busyHere} startedAt={runStartedAtMs} />
					</div>
				)}

				{/* Reconnect feed — server run still executing after a tab switch / reload. Suppressed while
				    an approval is in flight: approving inserts a phantom `running` run row server-side,
				    which would otherwise flash this for a run that never started. */}
				{!busyHere && liveEvents.length === 0 && !runResult && hasFreshServerRun && (
					<div className="tst-turn tst-measure">
						<RunLiveFeed events={reconnectEvents} isActive startedAt={runStartedAtMs} />
					</div>
				)}

				{/* Mid-run clarification — run paused, waiting for the user. */}
				{clarificationEvent && runningHere && (
					<div className="tst-turn tst-measure">
						<ClarificationPrompt
							runId={clarificationEvent.run_id ?? ""}
							failingLabel={clarificationEvent.failing_label ?? ""}
							suggestion={clarificationEvent.suggestion ?? ""}
							selectedPlanId={selectedPlanId ?? ""}
						/>
					</div>
				)}

				{/* The plan landed but the run continues — the gap Weave's "still working" strip covers. */}
				{busyHere && Boolean(runResult) && (
					<div className="tst-measure pb-3">
						<StillWorking label={healingHere ? "Preparing a fix proposal…" : "Running in the browser…"} />
					</div>
				)}

				<ChatSessionSummary runs={history} />

				{chat.error && (
					<div className="tst-measure pb-3">
						<div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive-strong">
							<div className="mb-1 flex items-center gap-1 font-medium">
								<AlertTriangle className="h-3 w-3 shrink-0" /> Error
							</div>
							{/* whitespace-pre-wrap keeps newlines (a multi-line stack was collapsing into one
							    mangled line); break-words wraps long paths; scroll for long stacks. */}
							<div className="max-h-52 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">
								{chat.error}
							</div>
						</div>
					</div>
				)}

				<JumpToLatest visible={showJump} streaming={chat.streaming || busyHere} onClick={jumpToLatest} />
			</div>

			<TestingComposer
				value={draft}
				onChange={setDraft}
				onSubmit={onChatSubmit}
				canSend={canChat}
				streaming={chat.streaming}
				onCancelStreaming={cancelStreaming}
				disabledReason={composerDisabledReason}
				editingPlanName={editingPlanId ? (editingPlanName ?? "this plan") : null}
				onCancelEdit={cancelEditMode}
			/>
		</div>
	);
}
